import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDictionary } from '@/lib/i18n'
import BrandMark from '@/components/generals/brand-mark'
import ButtonLink from '@/components/generals/button-link'
import LanguageSwitch from '@/components/generals/language-switch'

const SiteHeader: FC = (): ReactNode => {
    const t = useDictionary()
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        const previousOverflow = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', onKeyDown)

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [open])

    const close = (): void => setOpen(false)

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300',
                scrolled || open ? 'border-border bg-background/85 backdrop-blur-xl' : 'border-transparent bg-transparent'
            )}
        >
            <a
                href='#main'
                className='sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-[60] focus:rounded-full focus:bg-race focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white'
            >
                {t.nav.skip}
            </a>

            <div className='mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8'>
                <BrandMark />

                <nav aria-label={t.nav.primary} className='hidden xl:block'>
                    <ul className='flex items-center gap-7'>
                        {t.nav.links.map((link) => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    className='text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring'
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-3'>
                    <LanguageSwitch className='hidden sm:inline-flex' />

                    <ButtonLink href='/#register' className='hidden sm:inline-flex' size='md'>
                        {t.nav.cta}
                    </ButtonLink>

                    <button
                        type='button'
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        onClick={() => setOpen((value) => !value)}
                        className='inline-flex size-11 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring xl:hidden'
                    >
                        {open ? <X aria-hidden='true' className='size-5' /> : <Menu aria-hidden='true' className='size-5' />}

                        <span className='sr-only'>
                            {open ? t.nav.closeMenu : t.nav.openMenu}
                        </span>
                    </button>
                </div>
            </div>

            {open && (
                <div
                    id='mobile-menu'
                    className='h-[calc(100svh-4rem)] overflow-y-auto border-t border-border bg-background px-5 pb-10 pt-6 sm:px-8 xl:hidden animate-in fade-in slide-in-from-top-2 duration-200'
                >
                    <nav aria-label={t.nav.primary}>
                        <ul className='divide-y divide-border'>
                            {t.nav.links.map((link, index) => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        onClick={close}
                                        className='flex items-baseline gap-4 py-4 font-heading text-3xl font-semibold uppercase tracking-tight text-foreground transition-colors hover:text-race-bright'
                                    >
                                        <span className='font-mono text-xs text-muted-foreground'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='mt-8 flex flex-col gap-4'>
                        <LanguageSwitch className='self-start' onSelect={close} />

                        <ButtonLink href='/#register' size='lg' onClick={close}>
                            {t.nav.cta}
                        </ButtonLink>
                    </div>
                </div>
            )}
        </header>
    )
}

export default SiteHeader
